import { useCallback, useEffect, useState } from "react"
import api from "../services/api"
import AdminLayout from "../components/AdminLayout"
import FileIcon from "../components/FileIcon"

const C = {
    navy: "#002B54",
    navyLight: "#163354",
    navyMid: "#1e4070",
    blue: "#1a6fc4",
    blueLight: "#3b8de0",
    bluePale: "#e8f0fb",
    bluePale2: "#dceaf8",
    white: "#ffffff",
    offWhite: "#f5f7fa",
    gray100: "#eef1f5",
    gray200: "#dde2ea",
    gray300: "#c3cad5",
    gray400: "#94a0b0",
    gray500: "#64748b",
    gray700: "#334155",
    green: "#15803d",
    greenBg: "#dcfce7",
    greenBorder: "#86efac",
    orange: "#F4811F",
    orangeBg: "#fff4eb",
    purple: "#7c3aed",
    purpleBg: "#f5f3ff",
    purpleBorder: "#c4b5fd",
}

const ROLE_STYLES = {
    admin: { bg: C.orangeBg, color: C.orange, border: "#fed7aa", label: "Admin" },
    customer: { bg: C.bluePale, color: C.blue, border: C.bluePale2, label: "Customer" },
    prospect: { bg: C.gray100, color: C.gray500, border: C.gray200, label: "Prospect" },
}

function RoleBadge({ role }) {
    const s = ROLE_STYLES[role] || ROLE_STYLES.prospect
    return (
        <span style={{
            display: "inline-flex", alignItems: "center", gap: "4px",
            padding: "3px 10px", borderRadius: "20px",
            fontSize: "10px", fontWeight: "800", textTransform: "uppercase",
            letterSpacing: "0.5px",
            background: s.bg, color: s.color, border: `1px solid ${s.border}`,
        }}>
            {s.label}
        </span>
    )
}

function Avatar({ email }) {
    const safeEmail = email || "Guest"
    const initials = safeEmail.slice(0, 2).toUpperCase()
    const colors = [
        { bg: "#dceaf8", text: "#1a6fc4" },
        { bg: "#e0f2fe", text: "#0369a1" },
        { bg: "#ede9fe", text: "#6d28d9" },
        { bg: "#fce7f3", text: "#be185d" },
        { bg: "#dcfce7", text: "#15803d" },
    ]
    const col = colors[safeEmail.charCodeAt(0) % colors.length]
    return (
        <div style={{
            width: "36px", height: "34px", borderRadius: "10px",
            background: col.bg, color: col.text,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "12px", fontWeight: "800", flexShrink: 0,
            letterSpacing: "0.5px",
        }}>
            {initials}
        </div>
    )
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

export default function LoginHistory() {
    const [records, setRecords] = useState([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(10)
    const [totalPages, setTotalPages] = useState(1)
    const [loading, setLoading] = useState(true)
    const [searchEmail, setSearchEmail] = useState("")
    const [filterRole, setFilterRole] = useState("")

    const loadData = useCallback(async () => {
        try {
            setLoading(true)
            const params = new URLSearchParams({ page, limit })
            if (searchEmail.trim()) params.set("email", searchEmail.trim())
            if (filterRole) params.set("role", filterRole)
            const res = await api.get(`/admin/login-history?${params}`)
            setRecords(res.data.records)
            setTotal(res.data.total)
            setTotalPages(res.data.totalPages)
        } catch (err) {
            console.error("Failed to load login history:", err)
        } finally {
            setLoading(false)
        }
    }, [page, limit, searchEmail, filterRole])

    useEffect(() => { loadData() }, [loadData])

    const handleSearch = () => {
        setPage(1)
        loadData()
    }

    function getPageNumbers() {
        const pages = []
        const maxVisible = 5
        if (totalPages <= maxVisible + 2) {
            for (let i = 1; i <= totalPages; i++) pages.push(i)
        } else {
            pages.push(1)
            let start = Math.max(2, page - 1)
            let end = Math.min(totalPages - 1, page + 1)
            if (page <= 3) { start = 2; end = Math.min(maxVisible, totalPages - 1) }
            else if (page >= totalPages - 2) { start = Math.max(totalPages - maxVisible + 1, 2); end = totalPages - 1 }
            if (start > 2) pages.push("...")
            for (let i = start; i <= end; i++) pages.push(i)
            if (end < totalPages - 1) pages.push("...")
            pages.push(totalPages)
        }
        return pages
    }

    const formatDate = (d) =>
        new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })

    const uniqueEmails = [...new Set(records.map(r => r.email))].length

    return (
        <AdminLayout title="Login History" description="Track all user login activity across the Trust Center.">
            <div style={{
                width: "100%", maxWidth: "none", margin: 0,
                fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                paddingBottom: "48px",
            }}>
                {/* Stats */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px", marginBottom: "24px" }}>
                    {[
                        { icon: null, num: total, label: "Total Logins", accent: C.blue, accentBg: C.bluePale },
                        { icon: null, num: uniqueEmails, label: "Unique Users", accent: C.purple, accentBg: C.purpleBg },
                        { icon: <FileIcon size={20} color={C.orange} />, num: totalPages, label: "Pages", accent: C.orange, accentBg: C.orangeBg },
                    ].map(({ icon, num, label, accent, accentBg }) => (
                        <div key={label} style={{
                            background: C.white, border: `1px solid ${C.gray200}`, borderRadius: "14px",
                            padding: "18px 22px", boxShadow: "0 1px 8px rgba(12,35,64,0.05)",
                            display: "flex", alignItems: "center", gap: "14px",
                        }}>
                            <div style={{
                                width: "44px", height: "44px", borderRadius: "12px",
                                background: accentBg,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: "11px", fontWeight: 800, color: accent, flexShrink: 0,
                            }}>{icon || label.slice(0, 1)}</div>
                            <div>
                                <div style={{ fontSize: "26px", fontWeight: "800", color: accent, lineHeight: 1 }}>{num}</div>
                                <div style={{ fontSize: "11px", fontWeight: "600", color: C.gray400, textTransform: "uppercase", letterSpacing: "0.5px", marginTop: "3px" }}>{label}</div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Table Card */}
                <div style={{
                    background: C.white, border: `1px solid ${C.gray200}`,
                    borderRadius: "16px", overflow: "hidden",
                    boxShadow: "0 2px 16px rgba(12,35,64,0.07)",
                }}>
                    {/* Header */}
                    <div style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "18px 24px", borderBottom: `1px solid ${C.gray100}`, background: C.offWhite,
                    }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <div style={{
                                width: "32px", height: "32px", borderRadius: "9px",
                                background: `linear-gradient(135deg, ${C.navy}, ${C.navyMid})`,
                                display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", fontWeight: 800, color: "#fff",
                            }}>LH</div>
                            <div>
                                <div style={{ fontSize: "14px", fontWeight: "800", color: C.navy }}>Login Records</div>
                                <div style={{ fontSize: "11px", color: C.gray400 }}>Every successful OTP verification is logged</div>
                            </div>
                        </div>
                    </div>

                    {/* Search / Filter Bar */}
                    <div style={{
                        padding: "12px 24px", borderBottom: `1px solid ${C.gray100}`, background: C.white,
                        display: "flex", alignItems: "center", gap: "12px",
                    }}>
                        <div style={{ fontSize: "12px", fontWeight: 700, color: C.gray500 }}>Search</div>
                        <input
                            type="text"
                            placeholder="Search by email..."
                            value={searchEmail}
                            onChange={(e) => setSearchEmail(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                            style={{
                                flex: 1, padding: "8px 12px", borderRadius: "8px",
                                border: `1px solid ${C.gray200}`, fontSize: "13px", color: C.navy, outline: "none",
                            }}
                        />
                        <select
                            value={filterRole}
                            onChange={(e) => { setFilterRole(e.target.value); setPage(1) }}
                            style={{
                                padding: "8px 12px", borderRadius: "8px",
                                border: `1px solid ${C.gray200}`, fontSize: "12px", fontWeight: "600",
                                color: C.navy, background: C.offWhite, cursor: "pointer", outline: "none",
                            }}
                        >
                            <option value="">All Roles</option>
                            <option value="admin">Admin</option>
                            <option value="customer">Customer</option>
                            <option value="prospect">Prospect</option>
                        </select>
                        <button onClick={handleSearch} style={{
                            padding: "8px 18px", borderRadius: "8px",
                            background: `linear-gradient(135deg, ${C.navy}, ${C.navyMid})`,
                            color: C.white, border: "none", fontSize: "12px", fontWeight: "700", cursor: "pointer",
                        }}>Search</button>
                        {searchEmail && (
                            <button onClick={() => { setSearchEmail(""); setPage(1); setTimeout(loadData, 0) }} style={{
                                background: "none", border: "none", color: C.gray400,
                                fontSize: "12px", fontWeight: "700", cursor: "pointer", padding: "4px 8px",
                            }}>Clear</button>
                        )}
                    </div>

                    {/* Table */}
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ borderBottom: `1.5px solid ${C.gray100}`, background: C.offWhite }}>
                                    {["User", "Role", "Location", "Device", "IP Address", "Browser", "Login Time"].map(h => (
                                        <th key={h} style={{
                                            padding: "12px 20px", fontSize: "10px", fontWeight: "800",
                                            textTransform: "uppercase", letterSpacing: "0.8px",
                                            color: C.gray400, textAlign: "left", whiteSpace: "nowrap",
                                        }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={7} style={{ padding: "60px 24px", textAlign: "center" }}>
                                            <p style={{ color: C.gray400, fontWeight: 600, fontSize: "14px" }}>Loading...</p>
                                        </td>
                                    </tr>
                                ) : records.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} style={{ padding: "60px 24px", textAlign: "center" }}>
                                            <p style={{ fontSize: "14px", color: C.gray400, fontStyle: "italic" }}>
                                                No login records found.
                                            </p>
                                        </td>
                                    </tr>
                                ) : records.map((r, idx) => {
                                    const locationStr = r.location
                                        ? [r.location.city, r.location.region, r.location.countryCode].filter(Boolean).join(", ")
                                        : ""
                                    const deviceLabel = r.deviceType ? r.deviceType.charAt(0).toUpperCase() + r.deviceType.slice(1) : "Unknown"

                                    return (
                                        <tr key={r._id} style={{
                                            borderBottom: idx === records.length - 1 ? "none" : `1px solid ${C.gray100}`,
                                            transition: "background 0.15s",
                                        }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = C.offWhite}
                                            onMouseLeave={(e) => e.currentTarget.style.background = C.white}
                                        >
                                            <td style={{ padding: "14px 20px" }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                                    <Avatar email={r.email} />
                                                    <div style={{ fontSize: "13px", fontWeight: "700", color: C.navy }}>{r.email}</div>
                                                </div>
                                            </td>
                                            <td style={{ padding: "14px 20px" }}><RoleBadge role={r.role} /></td>
                                            <td style={{ padding: "14px 20px" }}>
                                                {locationStr ? (
                                                    <span style={{ fontSize: "12px", color: C.gray700 }}>{locationStr}</span>
                                                ) : (
                                                    <span style={{ fontSize: "12px", color: C.gray300, fontStyle: "italic" }}>—</span>
                                                )}
                                            </td>
                                            <td style={{ padding: "14px 20px" }}>
                                                <div style={{
                                                    display: "inline-flex", alignItems: "center", gap: "5px",
                                                    padding: "4px 10px", borderRadius: "20px",
                                                    fontSize: "10px", fontWeight: "700", textTransform: "uppercase",
                                                    letterSpacing: "0.5px",
                                                    background: r.deviceType === "mobile" ? C.purpleBg : r.deviceType === "tablet" ? C.orangeBg : C.bluePale,
                                                    color: r.deviceType === "mobile" ? C.purple : r.deviceType === "tablet" ? C.orange : C.blue,
                                                    border: `1px solid ${r.deviceType === "mobile" ? C.purpleBorder : r.deviceType === "tablet" ? "#fed7aa" : C.bluePale}`
                                                }}>
                                                    {deviceLabel}
                                                </div>
                                            </td>
                                            <td style={{ padding: "14px 20px" }}>
                                                <span style={{ fontSize: "12px", color: C.gray700, fontFamily: "monospace" }}>
                                                    {r.ip || "—"}
                                                </span>
                                            </td>
                                            <td style={{ padding: "14px 20px", maxWidth: "200px" }}>
                                                <div style={{
                                                    fontSize: "11px", color: C.gray500,
                                                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                                }} title={r.userAgent}>
                                                    {r.userAgent ? r.userAgent.split("(")[0].trim() : "—"}
                                                </div>
                                            </td>
                                            <td style={{ padding: "14px 20px" }}>
                                                <div style={{ fontSize: "12px", color: C.gray700, fontWeight: "600" }}>
                                                    {formatDate(r.loginAt)}
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Footer */}
                    {records.length > 0 && (
                        <div style={{
                            padding: "14px 24px", borderTop: `1px solid ${C.gray100}`, background: C.offWhite,
                            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px",
                        }}>
                            {/* Left: rows per page + info */}
                            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    <span style={{ fontSize: "12px", color: C.gray500, fontWeight: "600" }}>Rows per page:</span>
                                    <select
                                        value={limit}
                                        onChange={(e) => { setLimit(Number(e.target.value)); setPage(1) }}
                                        style={{
                                            padding: "4px 8px", borderRadius: "6px",
                                            border: `1px solid ${C.gray200}`, fontSize: "12px", fontWeight: "700",
                                            color: C.navy, background: C.white, cursor: "pointer", outline: "none",
                                        }}
                                    >
                                        {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                </div>
                                <span style={{ fontSize: "12px", color: C.gray400 }}>
                                    {Math.min((page - 1) * limit + 1, total)}–{Math.min(page * limit, total)} of {total}
                                </span>
                            </div>

                            {/* Right: page numbers */}
                            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                <button
                                    onClick={() => setPage(1)}
                                    disabled={page <= 1}
                                    title="First page"
                                    style={{
                                        width: "32px", height: "32px", borderRadius: "8px",
                                        border: `1px solid ${C.gray200}`, background: C.white,
                                        fontSize: "13px", color: page <= 1 ? C.gray300 : C.navy,
                                        cursor: page <= 1 ? "not-allowed" : "pointer",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                    }}
                                >«</button>
                                <button
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page <= 1}
                                    title="Previous page"
                                    style={{
                                        width: "32px", height: "32px", borderRadius: "8px",
                                        border: `1px solid ${C.gray200}`, background: C.white,
                                        fontSize: "13px", color: page <= 1 ? C.gray300 : C.navy,
                                        cursor: page <= 1 ? "not-allowed" : "pointer",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                    }}
                                >‹</button>

                                {getPageNumbers().map((p, i) =>
                                    p === "..." ? (
                                        <span key={`ellipsis-${i}`} style={{
                                            width: "32px", height: "32px",
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            fontSize: "12px", color: C.gray400, fontWeight: "700",
                                        }}>…</span>
                                    ) : (
                                        <button
                                            key={p}
                                            onClick={() => setPage(p)}
                                            style={{
                                                width: "32px", height: "32px", borderRadius: "8px",
                                                border: p === page ? "none" : `1px solid ${C.gray200}`,
                                                background: p === page ? `linear-gradient(135deg, ${C.navy}, ${C.navyMid})` : C.white,
                                                fontSize: "12px", fontWeight: "700",
                                                color: p === page ? C.white : C.navy,
                                                cursor: "pointer",
                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                transition: "all 0.15s",
                                            }}
                                        >{p}</button>
                                    )
                                )}

                                <button
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page >= totalPages}
                                    title="Next page"
                                    style={{
                                        width: "32px", height: "32px", borderRadius: "8px",
                                        border: `1px solid ${C.gray200}`, background: C.white,
                                        fontSize: "13px", color: page >= totalPages ? C.gray300 : C.navy,
                                        cursor: page >= totalPages ? "not-allowed" : "pointer",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                    }}
                                >›</button>
                                <button
                                    onClick={() => setPage(totalPages)}
                                    disabled={page >= totalPages}
                                    title="Last page"
                                    style={{
                                        width: "32px", height: "32px", borderRadius: "8px",
                                        border: `1px solid ${C.gray200}`, background: C.white,
                                        fontSize: "13px", color: page >= totalPages ? C.gray300 : C.navy,
                                        cursor: page >= totalPages ? "not-allowed" : "pointer",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                    }}
                                >»</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </AdminLayout>
    )
}
